import React from 'react';

/**
 * AIAssistantTool - Local Enterprise AI Assistant
 * Provides AI chat interface for operational data analysis
 */
export const AIAssistantTool: React.FC = () => {
  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">AI Assistant</h2>
          <p className="text-gray-600">Local Enterprise AI & Intelligent Automation Platform</p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* AI Chat Interface */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#0054A4]/10 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Local AI Chat</h3>
              <span className="ml-auto px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Online
              </span>
            </div>

            {/* Chat Messages Area */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 h-96 overflow-y-auto border border-gray-100">
              <div className="space-y-4">
                {/* AI Message */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#0054A4]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-700 text-sm">Hello! I'm your local enterprise AI assistant. I'm running on-premises to ensure your data security and privacy. How can I help you today?</p>
                    </div>
                    <span className="text-xs text-gray-500 mt-1 block">Local Model • Just now</span>
                  </div>
                </div>

                {/* User Message Example */}
                <div className="flex gap-3 justify-end">
                  <div className="flex-1 max-w-md">
                    <div className="bg-[#0054A4] rounded-lg p-3">
                      <p className="text-white text-sm">What operational data can you analyze?</p>
                    </div>
                    <span className="text-xs text-gray-500 mt-1 block text-right">You • 2 min ago</span>
                  </div>
                </div>

                {/* AI Response */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#0054A4]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-700 text-sm mb-2">I can analyze various enterprise operational data including:</p>
                      <ul className="text-gray-600 text-sm space-y-1 ml-4 list-disc">
                        <li>SCADA and OT system metrics</li>
                        <li>Manufacturing and refinery KPIs</li>
                        <li>Incident patterns and trends</li>
                        <li>Asset performance data</li>
                        <li>Service deployment logs</li>
                      </ul>
                      <p className="text-gray-700 text-sm mt-2">All processing happens locally on your infrastructure.</p>
                    </div>
                    <span className="text-xs text-gray-500 mt-1 block">Local Model • 1 min ago</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Input Area */}
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Ask about operations, incidents, or system data..."
                className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0054A4]"
              />
              <button className="bg-[#0054A4] hover:bg-[#003A73] text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Send
              </button>
            </div>
          </div>

          {/* AI Capabilities Sidebar */}
          <div className="space-y-6">
            {/* Model Info */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Local AI Model</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Model Type:</span>
                  <span className="text-sm text-gray-900 font-medium">Enterprise LLM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Deployment:</span>
                  <span className="text-sm text-green-600 font-medium">On-Premises</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Privacy:</span>
                  <span className="text-sm text-[#0054A4] font-medium">100% Local</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Context Size:</span>
                  <span className="text-sm text-gray-900 font-medium">128K tokens</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm py-2.5 px-4 rounded-lg transition-colors text-left flex items-center gap-2 border border-gray-200">
                  <svg className="w-4 h-4 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Analyze Operational Data
                </button>
                <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm py-2.5 px-4 rounded-lg transition-colors text-left flex items-center gap-2 border border-gray-200">
                  <svg className="w-4 h-4 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Generate Report
                </button>
                <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm py-2.5 px-4 rounded-lg transition-colors text-left flex items-center gap-2 border border-gray-200">
                  <svg className="w-4 h-4 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Automate Workflow
                </button>
                <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm py-2.5 px-4 rounded-lg transition-colors text-left flex items-center gap-2 border border-gray-200">
                  <svg className="w-4 h-4 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search Knowledge Base
                </button>
              </div>
            </div>

            {/* Recent Queries */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Queries</h3>
              <div className="space-y-3">
                <div className="text-sm">
                  <p className="text-gray-700">Refinery A uptime analysis</p>
                  <span className="text-xs text-gray-500">15 min ago</span>
                </div>
                <div className="text-sm">
                  <p className="text-gray-700">SCADA alarm trends</p>
                  <span className="text-xs text-gray-500">1 hour ago</span>
                </div>
                <div className="text-sm">
                  <p className="text-gray-700">Production KPI summary</p>
                  <span className="text-xs text-gray-500">3 hours ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantTool;
